import apiClient from '@/api/apiClient';
import type { CnaeList, CnaeListCreate, CnaeListUpdate } from '@/types/cnaeList';
import type { ProfileGroup, ProfileGroupDeleteImpact, ProfileGroupListParams, PaginatedProfileGroups } from '@/types/risk-profile';


const accountAdminApi = {
  async getKyxRiskModelOptions(): Promise<any> {
    try {
      return await apiClient.get('/account-admin/config/kyx-risk-model-options');
    } catch (error) {
      console.error('Get Risk Profile Config Options:', error);
      throw error;
    }
  },

  async createRiskModel(riskModelData: any): Promise<any> {
    try {
      return await apiClient.post('/account-admin/', riskModelData);
    } catch (error) {
      console.error('Create Risk Model:', error);
      throw error;
    }
  },

  async getProfileGroups(params: ProfileGroupListParams = {}): Promise<PaginatedProfileGroups> {
    try {
      return await apiClient.get('/account-admin/profile-groups', { params: params as Record<string, any> });
    } catch (error) {
      console.error('Get Profile Groups:', error);
      throw error;
    }
  },

  async getProfileGroup(groupId: string): Promise<ProfileGroup> {
    try {
      return await apiClient.get(`/account-admin/profile-groups/${groupId}`);
    } catch (error) {
      console.error('Get Profile Group:', error);
      throw error;
    }
  },

  async getActiveRiskModelTemplates(): Promise<ProfileGroup[]> {
    try {
      return await apiClient.get('/account-admin/risk-model-templates/active');
    } catch (error) {
      console.error('Get Active Risk Model Templates:', error);
      throw error;
    }
  },

  async deactivateRiskModel(groupId: number | string): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/profile-groups/${groupId}/deactivate`, {});
    } catch (error) {
      console.error('Deactivate Risk Model:', error);
      throw error;
    }
  },

  async activateRiskModel(groupId: number | string): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/profile-groups/${groupId}/activate`, {});
    } catch (error) {
      console.error('Activate Risk Model:', error);
      throw error;
    }
  },

  async createDraftInstance(groupId: number | string, draftData: any): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/profile-groups/${groupId}/instances`, draftData);
    } catch (error) {
      console.error('Create Draft Instance:', error);
      throw error;
    }
  },

  async promoteToProduction(groupId: number | string, instanceId: number | string): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/profile-groups/${groupId}/instances/${instanceId}/promote`, {});
    } catch (error) {
      console.error('Promote to Production:', error);
      throw error;
    }
  },

  async setDesignatedInstance(groupId: number | string, instanceId: number | string): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/profile-groups/${groupId}/set-designated-instance/${instanceId}`, {});
    } catch (error) {
      console.error('Set Designated Instance:', error);
      throw error;
    }
  },

  async setReleaseAsProduction(groupId: number | string, instanceId: number | string): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/profile-groups/${groupId}/set-as-production/${instanceId}`, {});
    } catch (error) {
      console.error('Set Release as Production:', error);
      throw error;
    }
  },

  async deleteProfileGroup(groupId: number | string): Promise<any> {
    try {
      return await apiClient.delete(`/account-admin/profile-groups/${groupId}`);
    } catch (error) {
      console.error('Delete Profile Group:', error);
      throw error;
    }
  },

  async getProfileGroupDeleteImpact(groupId: number | string): Promise<ProfileGroupDeleteImpact> {
    try {
      return await apiClient.get(`/account-admin/profile-groups/${groupId}/delete-impact`);
    } catch (error) {
      console.error('Get Profile Group Delete Impact:', error);
      throw error;
    }
  },

    async getCnaeLists(): Promise<CnaeList[]> {
        return apiClient.get('/account-admin/cnae-lists');
    },

    async createCnaeList(newList: CnaeListCreate): Promise<CnaeList> {
        return apiClient.post('/account-admin/cnae-lists', newList);
    },

    async updateCnaeList(id: string, updates: CnaeListUpdate): Promise<CnaeList> {
        return apiClient.put(`/account-admin/cnae-lists/${id}`, updates);
    },

    async deleteCnaeList(id: string): Promise<void> {
        return apiClient.delete(`/account-admin/cnae-lists/${id}`);
    },

    async addCnaesToList(listId: string, cnaeCodes: string[]): Promise<CnaeList> {
        const items = cnaeCodes.map(cnae_code => ({ cnae_code }));
        return apiClient.post(`/account-admin/cnae-lists/${listId}/items`, items);
    },

    async removeCnaesFromList(listId: string, cnaeItemIds: string[]): Promise<CnaeList> {
        return apiClient.delete(`/account-admin/cnae-lists/${listId}/items`, { body: cnaeItemIds });
    },
};

export default accountAdminApi;
